'use client';
import { useEffect, useState } from 'react';
import { useResource } from '@/lib/use-resource';

interface Earning {
  symbol: string;
  name?: string;
  date: string;
  time: string;
  epsEstimate: number | null;
  epsActual: number | null;
  revenueEstimate: number | null;
}

function getDateStr(offset: number) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().split('T')[0];
}

function formatDate(offset: number) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getLabel(offset: number) {
  if (offset === 0) return 'Today';
  if (offset === -1) return 'Yesterday';
  if (offset === 1) return 'Tomorrow';
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toLocaleDateString('en-US', { weekday: 'short' });
}

interface EarningsCalendarProps {
  onSelectSymbol?: (symbol: string) => void;
  selectedSymbol?: string | null;
}

// Poll every 60s so actuals appear within a minute of FMP indexing them.
// Important on AMC days when META/MSFT/GOOGL/AMZN print at 4:00–4:30 PM ET.
const POLL_INTERVAL_MS = 60_000;

export function EarningsCalendar({ onSelectSymbol, selectedSymbol }: EarningsCalendarProps = {}) {
  const [result, setResult] = useState<{ key: string; earnings: Earning[] } | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [dayOffset, setDayOffset] = useState(0);
  const [pollTick, setPollTick] = useState(0);

  // Portfolio holdings — shared via useResource cache so other components
  // hitting /api/portfolio don't re-fetch.
  const { data: portfolioData } = useResource<{ symbols?: string[] }>('/api/portfolio');
  const portfolioSet = new Set(portfolioData?.symbols ?? []);

  // Tick every POLL_INTERVAL_MS so we re-fetch FMP and pick up newly-indexed
  // actuals without a page reload.
  useEffect(() => {
    const id = setInterval(() => setPollTick((p) => p + 1), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  // Loading is derived from "request-key hasn't returned yet" — no synchronous
  // setState in effect body, only inside async callbacks. Re-runs whenever
  // dayOffset OR pollTick changes (poll keeps the panel live).
  const requestKey = getDateStr(dayOffset);
  useEffect(() => {
    let cancelled = false;
    fetch(`/api/earnings-calendar?date=${requestKey}`)
      .then(r => {
        if (!r.ok) throw new Error('API error');
        return r.json();
      })
      .then((d: { earnings?: Earning[] }) => {
        if (cancelled) return;
        const earnings = Array.isArray(d?.earnings) ? d.earnings : [];
        setResult({ key: requestKey, earnings });
      })
      .catch(() => { if (!cancelled) setResult({ key: requestKey, earnings: [] }); });
    return () => { cancelled = true; };
  }, [requestKey, pollTick]);

  // Portfolio reporters at the top, then everyone else. Stable inside each
  // bucket (preserves the API's BMO-then-AMC ordering).
  const earningsRaw = result && result.key === requestKey ? result.earnings : [];
  const earnings = (() => {
    const portfolio: Earning[] = [];
    const rest: Earning[] = [];
    for (const e of earningsRaw) {
      if (portfolioSet.has(e.symbol)) portfolio.push(e);
      else rest.push(e);
    }
    return [...portfolio, ...rest];
  })();
  const portfolioCount = earnings.filter(e => portfolioSet.has(e.symbol)).length;
  const loading = !result || result.key !== requestKey;

  return (
    <div className="absolute right-4 bottom-12 bg-[#0a0c10]/95 backdrop-blur border border-[#1e293b] rounded-md w-36 max-h-[45vh] overflow-hidden flex flex-col z-50 pointer-events-auto">
      {/* Up arrow */}
      <button
        onClick={() => setDayOffset(prev => prev - 1)}
        className="flex items-center justify-center py-0.5 hover:bg-[#1e293b]/40 transition-colors text-[#475569] hover:text-[#94a3b8]"
      >
        <span className="text-[8px]">▲</span>
      </button>

      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between px-2 py-1.5 hover:bg-[#1e293b]/30 transition-colors border-t border-b border-[#1e293b]/40"
      >
        <div>
          <div className="text-[8px] uppercase tracking-wider text-[#475569]">
            Earnings · {getLabel(dayOffset)}
          </div>
          <div className="text-[11px] font-semibold text-white">
            {loading ? '...' : (
              <>
                {formatDate(dayOffset)} · {earnings.length}
                {portfolioCount > 0 && (
                  <span className="ml-1 text-[#38bdf8]">· {portfolioCount}★</span>
                )}
              </>
            )}
          </div>
        </div>
        <div className={`text-[#475569] transition-transform text-[8px] ${expanded ? 'rotate-180' : ''}`}>
          ▼
        </div>
      </button>

      {/* List */}
      {expanded && (
        <div className="overflow-y-auto flex-1 border-b border-[#1e293b]/40">
          {loading ? (
            <div className="p-2 text-[9px] text-[#475569] text-center">Loading...</div>
          ) : earnings.length === 0 ? (
            <div className="p-2 text-[9px] text-[#475569] text-center">None scheduled</div>
          ) : earnings.map((e) => {
            const beat = e.epsActual != null && e.epsEstimate != null && e.epsActual > e.epsEstimate;
            const miss = e.epsActual != null && e.epsEstimate != null && e.epsActual < e.epsEstimate;

            const isSelected = selectedSymbol === e.symbol;
            const clickable = !!onSelectSymbol;
            const isPortfolio = portfolioSet.has(e.symbol);
            return (
              <div
                key={e.symbol}
                onClick={clickable ? () => onSelectSymbol!(e.symbol) : undefined}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={clickable ? (ev) => {
                  if (ev.key === 'Enter' || ev.key === ' ') {
                    ev.preventDefault();
                    onSelectSymbol!(e.symbol);
                  }
                } : undefined}
                className={`px-2 py-1.5 border-b border-[#1e293b]/30 transition-colors ${
                  clickable ? 'cursor-pointer' : ''
                } ${
                  isSelected
                    ? 'bg-[#1e293b]/60'
                    : isPortfolio
                    ? 'bg-[#38bdf8]/10 hover:bg-[#38bdf8]/20'
                    : 'hover:bg-[#1e293b]/20'
                } ${isPortfolio ? 'border-l-2 border-l-[#38bdf8]' : ''}`}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className={`text-[11px] font-bold shrink-0 ${isPortfolio ? 'text-[#38bdf8]' : 'text-white'}`}>
                    {isPortfolio && <span className="mr-1">★</span>}
                    {e.symbol}
                  </span>
                  <div className="flex items-center gap-1 shrink-0">
                    {e.time && (
                      <span
                        className={`text-[7px] px-1 py-px rounded font-semibold ${
                          e.time === 'bmo'
                            ? 'bg-blue-500/15 text-blue-400'
                            : e.time === 'amc'
                            ? 'bg-purple-500/15 text-purple-400'
                            : 'bg-[#1e293b] text-[#64748b]'
                        }`}
                        title={e.time === 'bmo' ? 'Before market open' : e.time === 'amc' ? 'After market close' : ''}
                      >
                        {e.time === 'bmo' ? 'AM' : e.time === 'amc' ? 'PM' : ''}
                      </span>
                    )}
                  </div>
                </div>
                {e.name && (
                  <div className="text-[9px] text-[#94a3b8] truncate mt-0.5" title={e.name}>
                    {e.name}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-0.5">
                  {e.epsEstimate != null && (
                    <span className="text-[9px] text-[#64748b]">
                      EE: ${e.epsEstimate.toFixed(2)}
                    </span>
                  )}
                  {e.epsActual != null && (
                    <span className={`text-[9px] font-semibold ${
                      beat ? 'text-emerald-400' : miss ? 'text-red-400' : 'text-white'
                    }`}>
                      A: ${e.epsActual.toFixed(2)}
                      {beat && ' ✓'}
                      {miss && ' ✗'}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Down arrow */}
      <button
        onClick={() => setDayOffset(prev => prev + 1)}
        className="flex items-center justify-center py-0.5 hover:bg-[#1e293b]/40 transition-colors text-[#475569] hover:text-[#94a3b8]"
      >
        <span className="text-[8px]">▼</span>
      </button>
    </div>
  );
}
